import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Container,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import PrintIcon from "@mui/icons-material/Print";
import { authenticatedFetch } from "../../utils/api";
import { usePermissions } from "../../utils/permissions";
import DiaCircle from "../DiaCircle";
import GaugeChip from "../GaugeChip";
import { formatInteger, truncateText, PROGRAM_NAME_MAX_WIDTH, truncatedTextStyle } from "../../utils/formatters";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface OrgSettings {
  gstin: string;
  phone_number: string;
  org_name: string;
  address: string;
  email: string;
  state_code: string;
}

interface Company {
  id: number;
  name: string;
}

interface ProgramDetail {
  id: number;
  dia: number;
  gauge: string;
  program_type: string;
  weight_in_kilo: number;
  status?: string;
  completed_weight?: number;
}

interface Program {
  id: number;
  lot?: string;
  program_details?: ProgramDetail[];
}

interface DeliveryDetail {
  id?: number;
  dia: string;
  gauge: string;
  machine_type: string;
  rolls: number;
  weight: number;
  _destroy?: boolean;
}

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
}

interface Delivery {
  id: number;
  company_id: number;
  company: {
    id: number;
    name: string;
  };
  program_id: number;
  program: {
    id: number;
    lot?: string;
    total_weight?: number;
  };
  delivery_person_name: string;
  delivery_person_phone: string;
  delivery_vehicle_number: string;
  total_rolls?: number;
  total_weight?: number;
  calculated_total_rolls: number;
  calculated_total_weight: number | string;
  specs_display: string;
  delivery_details: DeliveryDetail[];
  created_at: string;
}

const deliveryDetailSchema = z.object({
  id: z.number().optional(),
  dia: z.string().min(1, "Dia is required"),
  gauge: z.string().min(1, "Gauge is required"),
  machine_type: z.string().min(1, "Machine type is required"),
  rolls: z
    .number({ required_error: "Rolls is required" })
    .int("Rolls must be an integer")
    .positive("Rolls must be positive"),
  weight: z
    .number({ required_error: "Weight is required" })
    .positive("Weight must be positive"),
  _destroy: z.boolean().optional(),
});

const deliverySchema = z.object({
  company_id: z.number().int(),
  program_id: z.number().int(),
  delivery_person_name: z.string().min(1, "Delivery person name is required"),
  delivery_person_phone: z.string().min(1, "Delivery person phone is required"),
  delivery_vehicle_number: z.string().min(1, "Vehicle number is required"),
  total_rolls: z.number().optional(),
  total_weight: z.number().optional(),
  delivery_details_attributes: z
    .array(deliveryDetailSchema)
    .min(1, "At least one delivery detail is required"),
});

type DeliveryFormData = z.infer<typeof deliverySchema>;

export default function DeliveryDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { canUpdate, canDelete } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<Program[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [orgSettings, setOrgSettings] = useState<OrgSettings | null>(null);

  const { control, handleSubmit, reset, watch } = useForm<DeliveryFormData>({
    resolver: zodResolver(deliverySchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "delivery_details_attributes",
  });

  const deliveryDetails = watch("delivery_details_attributes");
  const selectedCompanyId = watch("company_id");
  const selectedProgramId = watch("program_id");

  useEffect(() => {
    fetchDelivery();
    fetchCompanies();
    fetchPrograms();
    fetchMachines();
    fetchOrgSettings();
  }, [id]);

  const fetchOrgSettings = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/organization_settings`);
      if (response.ok) {
        const data = await response.json();
        setOrgSettings(data);
      }
    } catch (err) {
      console.error("Error fetching organization settings:", err);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      const filtered = programs.filter(
        (program: any) => program.company?.id === selectedCompanyId
      );
      setFilteredPrograms(filtered);
    } else {
      setFilteredPrograms([]);
    }
  }, [selectedCompanyId, programs]);

  const fetchDelivery = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/deliveries/${id}`);
      if (!response.ok) {
        throw new Error("Failed to fetch delivery");
      }

      const data = await response.json();
      setDelivery(data);
      reset({
        company_id: data.company_id,
        program_id: data.program_id,
        delivery_person_name: data.delivery_person_name,
        delivery_person_phone: data.delivery_person_phone,
        delivery_vehicle_number: data.delivery_vehicle_number,
        total_rolls: data.total_rolls,
        total_weight: data.total_weight,
        delivery_details_attributes: data.delivery_details.map(
          (d: DeliveryDetail) => ({
            id: d.id,
            dia: d.dia,
            gauge: d.gauge,
            machine_type: d.machine_type,
            rolls: Number(d.rolls),
            weight: Number(d.weight),
          })
        ),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/companies?per_page=1000&minimal=true`);
      if (!response.ok) {
        throw new Error("Failed to fetch companies");
      }

      const data = await response.json();
      setCompanies(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/programs`);
      if (!response.ok) {
        throw new Error("Failed to fetch programs");
      }

      const data = await response.json();
      setPrograms(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/machines`);
      if (response.ok) {
        const data = await response.json();
        setMachines(data);
      }
    } catch (err) {
      console.error("Error fetching machines:", err);
    }
  };

  // Helper functions to get available options from program details
  const getAvailableDias = (
    programId: number | undefined,
    selectedGauge?: string,
    selectedMachineType?: string
  ) => {
    if (!programId) return [];
    const program = programs.find((p) => p.id === programId);
    if (!program || !program.program_details) return [];

    // Filter by gauge and/or machine type if provided
    let filteredDetails = program.program_details;
    if (selectedGauge) {
      filteredDetails = filteredDetails.filter((d) => d.gauge === selectedGauge);
    }
    if (selectedMachineType) {
      filteredDetails = filteredDetails.filter((d) => d.program_type === selectedMachineType);
    }

    // Get unique dia values from filtered program details
    const dias = Array.from(new Set(filteredDetails.map((d) => d.dia)));
    return dias.sort((a, b) => a - b);
  };

  const getAvailableGauges = (
    programId: number | undefined,
    selectedDia?: string,
    selectedMachineType?: string
  ) => {
    if (!programId) return [];
    const program = programs.find((p) => p.id === programId);
    if (!program || !program.program_details) return [];

    // Filter by dia and/or machine type if provided
    let filteredDetails = program.program_details;
    if (selectedDia) {
      filteredDetails = filteredDetails.filter((d) => d.dia.toString() === selectedDia);
    }
    if (selectedMachineType) {
      filteredDetails = filteredDetails.filter((d) => d.program_type === selectedMachineType);
    }

    // Get unique gauge values from filtered program details
    const gauges = Array.from(new Set(filteredDetails.map((d) => d.gauge)));
    return gauges.sort();
  };

  const getAvailableMachineTypes = (
    programId: number | undefined,
    selectedDia?: string,
    selectedGauge?: string
  ) => {
    if (!programId) return [];
    const program = programs.find((p) => p.id === programId);
    if (!program || !program.program_details) return [];

    // Filter by dia and/or gauge if provided
    let filteredDetails = program.program_details;
    if (selectedDia) {
      filteredDetails = filteredDetails.filter((d) => d.dia.toString() === selectedDia);
    }
    if (selectedGauge) {
      filteredDetails = filteredDetails.filter((d) => d.gauge === selectedGauge);
    }

    // Get unique machine type values from filtered program details
    const machineTypes = Array.from(new Set(filteredDetails.map((d) => d.program_type)));
    return machineTypes.sort();
  };

  const onSubmit = async (data: DeliveryFormData) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/deliveries/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ delivery: data }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      setIsEditing(false);
      fetchDelivery();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this delivery?")) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_URL}/deliveries/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete delivery");
      }

      navigate("/deliveries");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const calculateTotalWeight = () => {
    return deliveryDetails
      ?.filter((d: any) => !d._destroy)
      .reduce((sum: number, detail: any) => sum + (detail?.weight || 0), 0)
      .toFixed(2);
  };

  const calculateTotalRolls = () => {
    return deliveryDetails
      ?.filter((d: any) => !d._destroy)
      .reduce((sum: number, detail: any) => sum + (detail?.rolls || 0), 0);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!delivery) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">{t('deliveries.title')} {t('notFound')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Card sx={{ mt: 4 }} className="no-print">
        <CardContent>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "stretch" : "center",
              gap: isMobile ? 2 : 0,
            }}
          >
            <Typography variant="h5" component="h2">
              {t('deliveries.deliveryId')}{delivery.id}
            </Typography>
            <Box sx={{ 
              display: "flex", 
              flexWrap: "wrap",
              gap: 1,
              justifyContent: isMobile ? "flex-start" : "flex-end",
            }}>
              {!isEditing && (
                <IconButton
                  onClick={handlePrint}
                  color="primary"
                  title={t('print')}
                >
                  <PrintIcon />
                </IconButton>
              )}
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/deliveries")}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
              >
                {t('back')}
              </Button>
              {!isEditing && (canUpdate('deliveries') || canDelete('deliveries')) && (
                <>
                  {canUpdate('deliveries') && (
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                      variant="contained"
                      size={isMobile ? "small" : "medium"}
                    >
                      {t('edit')}
                    </Button>
                  )}
                  {canDelete('deliveries') && (
                    <Button
                      startIcon={<DeleteIcon />}
                      onClick={handleDelete}
                      variant="contained"
                      color="error"
                      size={isMobile ? "small" : "medium"}
                    >
                      {t('delete')}
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!isEditing ? (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('deliveries.company')}
                  </Typography>
                  <Typography
                    variant="body1"
                    gutterBottom
                    onClick={() => navigate(`/companies/${delivery.company.id}`)}
                    sx={{
                      cursor: "pointer",
                      color: "primary.main",
                      textDecoration: "underline",
                      "&:hover": { color: "primary.dark" },
                    }}
                  >
                    {delivery.company.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('deliveries.programLot')}
                  </Typography>
                  <Typography
                    variant="body1"
                    gutterBottom
                    onClick={() => navigate(`/programs/${delivery.program.id}`)}
                    sx={{
                      cursor: "pointer",
                      color: "primary.main",
                      textDecoration: "underline",
                      "&:hover": { color: "primary.dark" },
                      ...truncatedTextStyle(PROGRAM_NAME_MAX_WIDTH.desktop),
                    }}
                    title={`${delivery.program.lot || `${t('programs.programNumber')}${delivery.program.id}`} (${delivery.program.total_weight?.toLocaleString() || 0} kg)`}
                  >
                    {delivery.program.lot || `${t('programs.programNumber')}${delivery.program.id}`} ({delivery.program.total_weight?.toLocaleString() || 0} kg)
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('deliveries.deliveryPersonName')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {delivery.delivery_person_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('deliveries.deliveryPersonPhone')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {delivery.delivery_person_phone}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('deliveries.vehicleNumber')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {delivery.delivery_vehicle_number}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Date(delivery.created_at).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('calculatedTotalRolls')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <Chip label={formatInteger(delivery.calculated_total_rolls)} color="success" />
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('calculatedTotalWeight')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <Chip
                      label={`${Number(delivery.calculated_total_weight || 0).toFixed(2)} kg`}
                      color="success"
                    />
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                {t('deliveries.deliveryDetails')}
              </Typography>
              {isMobile ? (
                // Mobile: Card-based layout
                <Stack spacing={2}>
                  {delivery.delivery_details.map((detail, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                        <DiaCircle
                          dia={detail.dia}
                          gauge={detail.gauge}
                          machineType={detail.machine_type}
                          machines={machines}
                          size="small"
                        />
                        <GaugeChip gauge={detail.gauge} />
                        <Chip 
                          label={detail.machine_type.charAt(0).toUpperCase() + detail.machine_type.slice(1)} 
                          size="small"
                          color={detail.machine_type === 'rib' ? 'primary' : 'secondary'}
                        />
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">{t('deliveries.rolls')}</Typography>
                          <Typography variant="body1" fontWeight="medium">{detail.rolls}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">{t('deliveries.weightKg')}</Typography>
                          <Typography variant="body1" fontWeight="medium">{detail.weight} kg</Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                // Desktop: Table layout
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('programs.dia')}</TableCell>
                        <TableCell>{t('programs.gaugeGG')}</TableCell>
                        <TableCell>{t('deliveries.machineType')}</TableCell>
                        <TableCell>{t('deliveries.rolls')}</TableCell>
                        <TableCell>{t('deliveries.weightKg')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {delivery.delivery_details.map((detail, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <DiaCircle
                              dia={detail.dia}
                              gauge={detail.gauge}
                              machineType={detail.machine_type}
                              machines={machines}
                              size="small"
                            />
                          </TableCell>
                          <TableCell><GaugeChip gauge={detail.gauge} /></TableCell>
                          <TableCell>
                            <Chip 
                              label={detail.machine_type.charAt(0).toUpperCase() + detail.machine_type.slice(1)} 
                              size="small"
                              color={detail.machine_type === 'rib' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell>{detail.rolls}</TableCell>
                          <TableCell>{detail.weight}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="company_id"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                  >
                    <InputLabel>{t('deliveries.company')}</InputLabel>
                    <Select {...field} label={t('deliveries.company')}>
                      {companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <FormHelperText>
                        {fieldState.error.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="program_id"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    disabled={!selectedCompanyId}
                  >
                    <InputLabel>{t('deliveries.program')}</InputLabel>
                    <Select {...field} label={t('deliveries.program')}>
                      {filteredPrograms.map((program) => {
                        const programText = program.lot || `${t('programs.programNumber')}${program.id}`;
                        return (
                          <MenuItem key={program.id} value={program.id} title={programText}>
                            {truncateText(programText, PROGRAM_NAME_MAX_WIDTH.select)}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {fieldState.error && (
                      <FormHelperText>
                        {fieldState.error.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="delivery_person_name"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('deliveries.deliveryPersonName')}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="delivery_person_phone"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('deliveries.deliveryPersonPhone')}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="delivery_vehicle_number"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('deliveries.vehicleNumber')}
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Box sx={{ mt: 3, mb: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6">{t('deliveries.deliveryDetails')}</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      append({
                        dia: "",
                        gauge: "",
                        machine_type: "",
                        rolls: 0,
                        weight: 0,
                      })
                    }
                  >
                    {t('addDetail')}
                  </Button>
                </Stack>

                {isMobile ? (
                  // Mobile: Card-based layout for editing
                  <Stack spacing={2}>
                    {fields.map((field, index) => {
                      const selectedDia = watch(`delivery_details_attributes.${index}.dia`);
                      const selectedGauge = watch(`delivery_details_attributes.${index}.gauge`);
                      const selectedMachineType = watch(`delivery_details_attributes.${index}.machine_type`);
                      
                      const availableDias = getAvailableDias(selectedProgramId, selectedGauge, selectedMachineType);
                      const availableGauges = getAvailableGauges(selectedProgramId, selectedDia, selectedMachineType);
                      const availableMachineTypes = getAvailableMachineTypes(selectedProgramId, selectedDia, selectedGauge);

                      return (
                        <Paper key={field.id} variant="outlined" sx={{ p: 2, position: "relative" }}>
                          <IconButton
                            color="error"
                            onClick={() => {
                              if (field.id) {
                                const currentValues = watch("delivery_details_attributes");
                                currentValues[index]._destroy = true;
                                reset({
                                  ...watch(),
                                  delivery_details_attributes: currentValues,
                                });
                              } else {
                                remove(index);
                              }
                            }}
                            disabled={fields.length === 1}
                            size="small"
                            sx={{ position: "absolute", top: 8, right: 8 }}
                          >
                            <DeleteIcon />
                          </IconButton>

                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Item {index + 1}
                          </Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Controller
                                name={`delivery_details_attributes.${index}.dia`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    error={!!fieldState.error}
                                    disabled={!selectedProgramId}
                                  >
                                    <InputLabel>{t('programs.dia')}</InputLabel>
                                    <Select
                                      {...field}
                                      value={field.value || ""}
                                      label={t('programs.dia')}
                                    >
                                      <MenuItem value="" disabled>
                                        {!selectedProgramId ? "Select Program" : "Select"}
                                      </MenuItem>
                                      {availableDias.map((dia) => (
                                        <MenuItem key={dia} value={dia.toString()}>
                                          {dia}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {fieldState.error && (
                                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <Controller
                                name={`delivery_details_attributes.${index}.gauge`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    error={!!fieldState.error}
                                    disabled={!selectedProgramId}
                                  >
                                    <InputLabel>{t('programs.gaugeGG')}</InputLabel>
                                    <Select
                                      {...field}
                                      value={field.value || ""}
                                      label={t('programs.gaugeGG')}
                                    >
                                      <MenuItem value="" disabled>
                                        {!selectedProgramId ? "Select Program" : "Select"}
                                      </MenuItem>
                                      {availableGauges.map((gauge) => (
                                        <MenuItem key={gauge} value={gauge}>
                                          {gauge} GG
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {fieldState.error && (
                                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name={`delivery_details_attributes.${index}.machine_type`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    error={!!fieldState.error}
                                    disabled={!selectedProgramId}
                                  >
                                    <InputLabel>{t('deliveries.machineType')}</InputLabel>
                                    <Select
                                      {...field}
                                      value={field.value || ""}
                                      label={t('deliveries.machineType')}
                                    >
                                      <MenuItem value="" disabled>
                                        {!selectedProgramId ? "Select Program First" : "Select Type"}
                                      </MenuItem>
                                      {availableMachineTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                          {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {fieldState.error && (
                                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <Controller
                                name={`delivery_details_attributes.${index}.rolls`}
                                control={control}
                                render={({
                                  field: { onChange, value, ...field },
                                  fieldState,
                                }) => (
                                  <TextField
                                    {...field}
                                    label={t('deliveries.rolls')}
                                    value={value || ""}
                                    onChange={(e) =>
                                      onChange(
                                        e.target.value
                                          ? Number(e.target.value)
                                          : undefined
                                      )
                                    }
                                    type="number"
                                    size="small"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <Controller
                                name={`delivery_details_attributes.${index}.weight`}
                                control={control}
                                render={({
                                  field: { onChange, value, ...field },
                                  fieldState,
                                }) => (
                                  <TextField
                                    {...field}
                                    label={t('deliveries.weightKg')}
                                    value={value || ""}
                                    onChange={(e) =>
                                      onChange(
                                        e.target.value
                                          ? Number(e.target.value)
                                          : undefined
                                      )
                                    }
                                    type="number"
                                    size="small"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      );
                    })}
                  </Stack>
                ) : (
                  // Desktop: Table layout
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('programs.dia')}</TableCell>
                          <TableCell>{t('programs.gaugeGG')}</TableCell>
                          <TableCell>{t('deliveries.machineType')}</TableCell>
                          <TableCell>{t('deliveries.rolls')}</TableCell>
                          <TableCell>{t('deliveries.weightKg')}</TableCell>
                          <TableCell width={60}>{t('action')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fields.map((field, index) => {
                          const selectedDia = watch(`delivery_details_attributes.${index}.dia`);
                          const selectedGauge = watch(`delivery_details_attributes.${index}.gauge`);
                          const selectedMachineType = watch(`delivery_details_attributes.${index}.machine_type`);
                          
                          const availableDias = getAvailableDias(selectedProgramId, selectedGauge, selectedMachineType);
                          const availableGauges = getAvailableGauges(selectedProgramId, selectedDia, selectedMachineType);
                          const availableMachineTypes = getAvailableMachineTypes(selectedProgramId, selectedDia, selectedGauge);

                          return (
                          <TableRow key={field.id}>
                            <TableCell>
                              <Controller
                                name={`delivery_details_attributes.${index}.dia`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    error={!!fieldState.error}
                                    disabled={!selectedProgramId}
                                  >
                                    <Select
                                      {...field}
                                      value={field.value || ""}
                                      displayEmpty
                                    >
                                      <MenuItem value="" disabled>
                                        {!selectedProgramId ? "Select Program First" : "Select Dia"}
                                      </MenuItem>
                                      {availableDias.map((dia) => (
                                        <MenuItem key={dia} value={dia.toString()}>
                                          {dia}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {fieldState.error && (
                                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`delivery_details_attributes.${index}.gauge`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    error={!!fieldState.error}
                                    disabled={!selectedProgramId}
                                  >
                                    <Select
                                      {...field}
                                      value={field.value || ""}
                                      displayEmpty
                                    >
                                      <MenuItem value="" disabled>
                                        {!selectedProgramId ? "Select Program First" : "Select Gauge"}
                                      </MenuItem>
                                      {availableGauges.map((gauge) => (
                                        <MenuItem key={gauge} value={gauge}>
                                          {gauge} GG
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {fieldState.error && (
                                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`delivery_details_attributes.${index}.machine_type`}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <FormControl
                                    fullWidth
                                    size="small"
                                    error={!!fieldState.error}
                                    disabled={!selectedProgramId}
                                  >
                                    <Select
                                      {...field}
                                      value={field.value || ""}
                                      displayEmpty
                                    >
                                      <MenuItem value="" disabled>
                                        {!selectedProgramId ? "Select Program First" : "Select Type"}
                                      </MenuItem>
                                      {availableMachineTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                          {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {fieldState.error && (
                                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`delivery_details_attributes.${index}.rolls`}
                                control={control}
                                render={({
                                  field: { onChange, value, ...field },
                                  fieldState,
                                }) => (
                                  <TextField
                                    {...field}
                                    value={value || ""}
                                    onChange={(e) =>
                                      onChange(
                                        e.target.value
                                          ? Number(e.target.value)
                                          : undefined
                                      )
                                    }
                                    type="number"
                                    size="small"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`delivery_details_attributes.${index}.weight`}
                                control={control}
                                render={({
                                  field: { onChange, value, ...field },
                                  fieldState,
                                }) => (
                                  <TextField
                                    {...field}
                                    value={value || ""}
                                    onChange={(e) =>
                                      onChange(
                                        e.target.value
                                          ? Number(e.target.value)
                                          : undefined
                                      )
                                    }
                                    type="number"
                                    size="small"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                color="error"
                                onClick={() => {
                                  if (field.id) {
                                    // Mark existing record for destruction
                                    const currentValues = watch(
                                      "delivery_details_attributes"
                                    );
                                    currentValues[index]._destroy = true;
                                    reset({
                                      ...watch(),
                                      delivery_details_attributes: currentValues,
                                    });
                                  } else {
                                    // Remove new record
                                    remove(index);
                                  }
                                }}
                                disabled={fields.length === 1}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>{t('calculatedTotalRolls')}:</strong> {calculateTotalRolls()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('calculatedTotalWeight')}:</strong> {calculateTotalWeight()} kg
                  </Typography>
                </Box>
              </Box>

              <Stack 
                direction={isMobile ? "column" : "row"} 
                spacing={2} 
                sx={{ mt: 3 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  fullWidth
                >
                  {t('saveChanges')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    setIsEditing(false);
                    fetchDelivery();
                  }}
                  fullWidth
                >
                  {t('cancel')}
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Printable Delivery Note - Only visible when printing */}
      {!isEditing && (
        <Paper
          elevation={0}
          className="printable-delivery-note"
          sx={{
            display: "none",
            "@media print": {
              display: "block !important",
            },
          }}
        >
          {/* Letterhead Container - Black & White Only */}
          <Box
            sx={{
              border: "2px solid #000",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Company Letterhead Header */}
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                borderBottom: "2px solid #000",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mb: 0.5 }}>
                <img 
                  src="/favicon-32x32.png" 
                  alt="Logo" 
                  style={{ width: "40px", height: "40px" }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "bold",
                    letterSpacing: "3px",
                    textTransform: "uppercase",
                  }}
                >
                  {orgSettings?.org_name || "DEVI IMPEX"}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                {orgSettings?.address || "Address not configured"}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 4, flexWrap: "wrap" }}>
                <Typography variant="body2">
                  Cell: {orgSettings?.phone_number || "N/A"}
                </Typography>
                <Typography variant="body2">
                  Email: {orgSettings?.email || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  GSTIN: {orgSettings?.gstin || "N/A"}
                </Typography>
              </Box>
            </Box>

            {/* Document Title */}
            <Box
              sx={{
                textAlign: "center",
                py: 1,
                borderBottom: "1px solid #000",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  letterSpacing: "2px",
                  textDecoration: "underline",
                }}
              >
                DELIVERY NOTE
              </Typography>
            </Box>

            {/* Document Info & Customer */}
            <Box sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}>
              {/* Document Number & Date Row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2,
                  pb: 1,
                  borderBottom: "1px dotted #000",
                }}
              >
                <Typography variant="h6">
                  <strong>D.C. No:</strong> {delivery.id}
                </Typography>
                <Typography variant="h6">
                  <strong>Date:</strong> {new Date(delivery.created_at).toLocaleDateString('en-GB')}
                </Typography>
              </Box>

              {/* Customer Name */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  To:
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  M/s. {delivery.company.name}
                </Typography>
              </Box>

              {/* Items Table */}
              <TableContainer sx={{ mb: 2, flex: 1 }}>
                <Table size="medium" sx={{ border: "1px solid #000" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          border: "1px solid #000", 
                          fontWeight: "bold",
                          textAlign: "center",
                          py: 1.5,
                          fontSize: "1rem",
                          width: "12%",
                        }}
                      >
                        Dia
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          border: "1px solid #000", 
                          fontWeight: "bold",
                          textAlign: "center",
                          py: 1.5,
                          fontSize: "1rem",
                          width: "15%",
                        }}
                      >
                        Gauge
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          border: "1px solid #000", 
                          fontWeight: "bold",
                          textAlign: "center",
                          py: 1.5,
                          fontSize: "1rem",
                          width: "15%",
                        }}
                      >
                        Type
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          border: "1px solid #000", 
                          fontWeight: "bold",
                          textAlign: "center",
                          py: 1.5,
                          fontSize: "1rem",
                          width: "15%",
                        }}
                      >
                        Rolls
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          border: "1px solid #000", 
                          fontWeight: "bold",
                          textAlign: "center",
                          py: 1.5,
                          fontSize: "1rem",
                          width: "18%",
                        }}
                      >
                        Weight (kg)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {delivery.delivery_details.map((detail, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ border: "1px solid #000", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                          {detail.dia}
                        </TableCell>
                        <TableCell sx={{ border: "1px solid #000", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                          {detail.gauge} GG
                        </TableCell>
                        <TableCell sx={{ border: "1px solid #000", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                          {detail.machine_type.charAt(0).toUpperCase() + detail.machine_type.slice(1)}
                        </TableCell>
                        <TableCell sx={{ border: "1px solid #000", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                          {detail.rolls}
                        </TableCell>
                        <TableCell sx={{ border: "1px solid #000", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                          {Number(detail.weight).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Empty rows to fill space - show at least 8 rows total */}
                    {delivery.delivery_details.length < 8 && Array.from({ length: 8 - delivery.delivery_details.length }).map((_, index) => (
                      <TableRow key={`empty-${index}`}>
                        <TableCell sx={{ border: "1px solid #000", py: 1.5 }}>&nbsp;</TableCell>
                        <TableCell sx={{ border: "1px solid #000", py: 1.5 }}>&nbsp;</TableCell>
                        <TableCell sx={{ border: "1px solid #000", py: 1.5 }}>&nbsp;</TableCell>
                        <TableCell sx={{ border: "1px solid #000", py: 1.5 }}>&nbsp;</TableCell>
                        <TableCell sx={{ border: "1px solid #000", py: 1.5 }}>&nbsp;</TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow>
                      <TableCell 
                        colSpan={3} 
                        sx={{ border: "1px solid #000", fontWeight: "bold", py: 1.5, fontSize: "1rem", textAlign: "right", pr: 2 }}
                      >
                        TOTAL:
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #000", fontWeight: "bold", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                        {delivery.calculated_total_rolls}
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #000", fontWeight: "bold", textAlign: "center", py: 1.5, fontSize: "1rem" }}>
                        {Number(delivery.calculated_total_weight).toFixed(2)} kg
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Delivery Person Details */}
              <Box 
                sx={{ 
                  py: 1, 
                  borderTop: "1px dotted #000",
                  borderBottom: "1px dotted #000",
                  mb: 2,
                }}
              >
                <Typography variant="body1">
                  <strong>Delivery Person:</strong> {delivery.delivery_person_name} &nbsp;|&nbsp; 
                  <strong>Phone:</strong> {delivery.delivery_person_phone} &nbsp;|&nbsp; 
                  <strong>Vehicle:</strong> {delivery.delivery_vehicle_number}
                </Typography>
              </Box>

              {/* Footer with Signatures */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: "auto",
                  pt: 2,
                }}
              >
                <Box sx={{ textAlign: "center", width: "40%" }}>
                  <Box sx={{ borderBottom: "1px solid #000", mb: 1, height: "50px" }} />
                  <Typography variant="body1">
                    Receiver's Signature
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center", width: "40%" }}>
                  <Box sx={{ borderBottom: "1px solid #000", mb: 1, height: "50px" }} />
                  <Typography variant="body1" fontWeight="bold">
                    For {orgSettings?.org_name || "DEVI IMPEX"}
                  </Typography>
                  <Typography variant="body2">
                    Authorised Signatory
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Print Styles */}
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            
            html, body {
              height: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            body * {
              visibility: hidden;
            }
            
            .printable-delivery-note, .printable-delivery-note * {
              visibility: visible !important;
            }
            
            .no-print, .MuiCard-root, .MuiAppBar-root, .MuiDrawer-root {
              display: none !important;
            }
            
            .MuiContainer-root {
              max-width: 100% !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            
            .printable-delivery-note {
              position: fixed !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              height: 100vh !important;
              box-shadow: none !important;
              padding: 0 !important;
              overflow: hidden !important;
              page-break-after: avoid !important;
              page-break-inside: avoid !important;
            }
            
            .printable-delivery-note > div {
              height: 100% !important;
              max-height: 100vh !important;
              overflow: hidden !important;
            }
          }
        `}
      </style>
    </Container>
  );
}

